import React, { useState, useCallback, useRef, useEffect } from 'react';
import { PCInstance, ConfigurationRelease, Room, MultiplayerSession, MultiplayerSessionStatus } from '../types/database';
import { MultiplayerOrchestrator, MultiplayerLaunchParams } from '../services/multiplayerOrchestrator';
import { createPCAgentClient } from '../services/pcAgent';
import { createVoiceChannel, moveUsersToVoiceChannel } from '../services/discord';
import { databaseService } from '../services/database';
import { dequeueForLaunch, isNotQueuedError } from '../services/queue';
import '../styles/multiplayer-session-modal.css';

interface MultiplayerSessionModalProps {
  experience: ConfigurationRelease;
  pcs: PCInstance[];
  room: Room;
  preSelectedPcId: string;
  operatorId?: string;
  isAdmin?: boolean;
  onClose: () => void;
  /** Customer-queue rows to pop (reason launched, no PC) once the session is
   *  running: multiplayer -> the orchestrator reports 'active'; VC mode ->
   *  every selected PC launched. Handed over by the dispatch pop-up. */
  dequeueEntryIds?: string[];
}

interface PcReadiness {
  busy: boolean;
  picoConnected: boolean;
  headsetCharged: boolean;
  // Agent autodiagnostics say launches are blocked (can_launch === false).
  // Old agents (no diagnostics block) never set this.
  diagBlocked: boolean;
  loaded: boolean;
}

const STATUS_LABELS: Record<MultiplayerSessionStatus, string> = {
  starting_server: 'Starting server...',
  waiting_for_server: 'Waiting for server to be ready...',
  server_ready: 'Server ready',
  launching_clients: 'Launching clients...',
  active: 'Session active',
  failed: 'Session failed',
  stopping: 'Stopping session...',
  completed: 'Session completed',
};

export default function MultiplayerSessionModal({
  experience,
  pcs,
  room,
  preSelectedPcId,
  operatorId,
  isAdmin = false,
  onClose,
  dequeueEntryIds,
}: MultiplayerSessionModalProps) {
  const isMultiplayer = !!experience.experiences?.is_multiplayer;
  const isVcMode = !isMultiplayer && !!experience.experiences?.use_vc;

  const minPlayers = isVcMode ? 1 : (experience.experiences?.min_players || 2);
  const maxPlayers = experience.experiences?.max_players || pcs.length;
  const supportedLanguages = experience.experiences?.supported_languages || [];
  const needsLanguageSelect = supportedLanguages.length >= 2;

  const [selectedPcIds, setSelectedPcIds] = useState<Set<string>>(new Set([preSelectedPcId]));
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(
    supportedLanguages.length === 1 ? supportedLanguages[0].language?.short_name || null : null
  );
  const [session, setSession] = useState<MultiplayerSession | null>(null);
  const [vcStatus, setVcStatus] = useState<{ phase: string; error?: string } | null>(null);
  const orchestratorRef = useRef<MultiplayerOrchestrator | null>(null);

  // The modal closes right after GO (launchAndClose) while the launch keeps
  // running in the background, so the dequeue lives in a ref-guarded closure
  // rather than in React state/effects that die with the component.
  const dequeuedRef = useRef(false);
  const dequeueQueueEntries = async () => {
    if (dequeuedRef.current || !dequeueEntryIds?.length || !operatorId) return;
    dequeuedRef.current = true;
    await Promise.allSettled(
      dequeueEntryIds.map(async (id) => {
        try {
          await dequeueForLaunch(id, operatorId, null);
        } catch (err) {
          if (!isNotQueuedError(err)) console.error('multiplayer: queue dequeue failed', err);
        }
      })
    );
  };

  // Identify server PC and base list of online PCs
  const serverPc = pcs.find((pc) => pc.is_dedicated_server);
  const basePcs = isVcMode
    ? pcs.filter((pc) => pc.status === 'online' && pc.ip_address)
    : pcs.filter((pc) => !pc.is_dedicated_server && pc.status === 'online' && pc.ip_address);

  // Per-PC readiness fetched from each PC's agent
  const [readiness, setReadiness] = useState<Record<string, PcReadiness>>({});

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      basePcs.map(async (pc) => {
        const client = createPCAgentClient([pc.ip_address, pc.tailscale_ip]);
        const status = await client.getStatus();
        const headsetBattery = status?.headset?.battery_level ?? 0;
        return [
          pc.id,
          {
            busy: !!status?.current_experience,
            picoConnected: !!status?.pico?.connected,
            headsetCharged: !!status?.headset?.connected && headsetBattery > 15,
            diagBlocked: status?.diagnostics?.can_launch === false,
            loaded: !!status,
          },
        ] as const;
      })
    ).then((entries) => {
      if (cancelled) return;
      setReadiness(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcs.length]);

  // Filter out busy and diagnostics-blocked PCs, plus (for non-admins) PCs
  // with no headset/pico ready
  const selectablePcs = basePcs.filter((pc) => {
    const r = readiness[pc.id];
    if (!r) return true; // not loaded yet — show so user sees them while loading
    if (r.busy) return false;
    if (r.diagBlocked) return false;
    if (!isAdmin && (!r.picoConnected || !r.headsetCharged)) return false;
    return true;
  });

  // Diagnostics-blocked PCs stay visible (unselectable) with a reason chip so
  // the operator sees why they're excluded — unlike busy PCs, this state can
  // silently persist and would otherwise look like a missing PC.
  const diagBlockedPcs = basePcs.filter((pc) => {
    const r = readiness[pc.id];
    return !!r && !r.busy && r.diagBlocked;
  });

  const togglePc = useCallback((pcId: string) => {
    setSelectedPcIds((prev) => {
      const next = new Set(prev);
      if (next.has(pcId)) {
        next.delete(pcId);
      } else {
        next.add(pcId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedPcIds(new Set(selectablePcs.map((pc) => pc.id)));
  }, [selectablePcs]);

  const deselectAll = useCallback(() => {
    setSelectedPcIds(new Set());
  }, []);

  const selectedCount = selectedPcIds.size;

  const canLaunch = isVcMode
    ? selectedCount >= 1 && (!needsLanguageSelect || selectedLanguage) && !vcStatus
    : serverPc && serverPc.status === 'online' &&
      selectedCount >= minPlayers && selectedCount <= maxPlayers &&
      (!needsLanguageSelect || selectedLanguage) && !session;

  const isActive = isVcMode
    ? vcStatus && !['done', 'failed'].includes(vcStatus.phase)
    : session && !['failed', 'completed'].includes(session.status);

  // Track created voice channel for cleanup
  const createdChannelRef = useRef<{ guildId: string; channelId: string } | null>(null);

  // --- VC Mode Launch ---
  const handleVcLaunch = async () => {
    if (selectedCount === 0) return;

    const selectedPcs = selectablePcs.filter((pc) => selectedPcIds.has(pc.id));
    setVcStatus({ phase: 'launching' });

    try {
      // Launch experience on all selected PCs in parallel (normal launch, not multiplayer)
      const results = await Promise.allSettled(
        selectedPcs.map(async (pc) => {
          const client = createPCAgentClient([pc.ip_address, pc.tailscale_ip]);
          return client.launchExperience({
            experience_id: experience.experience_id,
            version: experience.experience_release?.version,
            operator_id: operatorId,
            language: selectedLanguage || undefined,
          });
        })
      );

      const failures = results.filter(
        (r) => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success)
      );
      if (failures.length > 0) {
        setVcStatus({ phase: 'failed', error: `${failures.length}/${results.length} PCs failed to launch` });
        return;
      }
      void dequeueQueueEntries();

      // Set up Discord voice channel for selected PCs
      if (selectedCount >= 1) {
        setVcStatus({ phase: 'moving_to_vc' });
        console.log('[VC] Starting Discord VC flow, selectedCount:', selectedCount);

        // Get guild ID for this room's location
        const guildId = await databaseService.getGuildIdForRoom(room.id);
        console.log('[VC] Guild ID for room:', room.id, '->', guildId);
        if (!guildId) {
          console.warn('[VC] No Discord guild configured for this location');
          setVcStatus({ phase: 'done' });
          return;
        }

        // Create a temporary voice channel
        const channelName = `${experience.experiences?.name || 'Session'} ${new Date().toLocaleTimeString()}`;
        console.log('[VC] Creating temp voice channel:', channelName);
        const createResult = await createVoiceChannel(guildId, channelName);
        console.log('[VC] Create result:', createResult);

        if (!createResult.success || !createResult.channel_id) {
          console.warn('[VC] Failed to create voice channel:', createResult.error);
          setVcStatus({ phase: 'done' });
          return;
        }

        createdChannelRef.current = { guildId, channelId: createResult.channel_id };

        // Move Discord users into the temp channel via bot
        const userIds = selectedPcs
          .map((pc) => pc.discord_user_id)
          .filter((id): id is string => !!id);
        console.log('[VC] Moving users to channel:', createResult.channel_id, 'userIds:', userIds);

        if (userIds.length > 0) {
          const moveResult = await moveUsersToVoiceChannel(guildId, createResult.channel_id, userIds);
          console.log('[VC] Move result:', moveResult);
          if (!moveResult.success) {
            console.warn('[VC] Some users failed to move:', moveResult.errors);
          }
        }

        // Tell each PC's RealityAgent about the VC session so it cleans up when experience ends
        const lobbyChannelId = room.discord_lobby_channel_id;
        if (lobbyChannelId) {
          console.log('[VC] Setting VC session on each PC for cleanup on experience end');
          const rosterUserIds = selectedPcs
            .map((pc) => pc.discord_user_id)
            .filter((id): id is string => !!id);
          await Promise.allSettled(
            selectedPcs.map(async (pc) => {
              if (!pc.discord_user_id) return;
              const client = createPCAgentClient([pc.ip_address, pc.tailscale_ip]);
              return client.setVcSession(
                guildId,
                createResult.channel_id!,
                lobbyChannelId,
                pc.discord_user_id,
                rosterUserIds
              );
            })
          );
        } else {
          console.warn('[VC] No lobby channel configured on room — cleanup on experience end will not work');
        }
      }

      setVcStatus({ phase: 'done' });
    } catch (err: any) {
      setVcStatus({ phase: 'failed', error: err.message || 'Unknown error' });
    }
  };

  // --- Multiplayer Mode Launch ---
  const handleMultiplayerLaunch = async () => {
    if (!serverPc || selectedCount === 0) return;

    const selectedClientPcs = selectablePcs.filter((pc) => selectedPcIds.has(pc.id));

    const orchestrator = new MultiplayerOrchestrator((updatedSession) => {
      setSession({ ...updatedSession });
      if (updatedSession.status === 'active') void dequeueQueueEntries();
    });
    orchestratorRef.current = orchestrator;

    const params: MultiplayerLaunchParams = {
      experienceId: experience.experience_id,
      experienceName: experience.experiences?.name || 'Unknown',
      roomId: room.id,
      version: experience.experience_release?.version,
      serverPc,
      clientPcs: selectedClientPcs,
      operatorId,
      language: selectedLanguage || undefined,
    };

    await orchestrator.startSession(params);
  };

  const launchAndClose = () => {
    // Fire the launch in the background and close the modal immediately.
    // Session progress is observable from the PC tiles.
    void (isVcMode ? handleVcLaunch() : handleMultiplayerLaunch());
    onClose();
  };

  useEffect(() => {
    if (isVcMode && vcStatus?.phase === 'done') {
      const t = setTimeout(onClose, 800);
      return () => clearTimeout(t);
    }
  }, [isVcMode, vcStatus?.phase, onClose]);

  const handleStop = async () => {
    if (!orchestratorRef.current || !serverPc) return;
    const selectedClientPcs = selectablePcs.filter((pc) => selectedPcIds.has(pc.id));
    await orchestratorRef.current.stopSession(serverPc, selectedClientPcs);
  };

  const handleClose = () => {
    if (isActive) return;
    // Just close the modal — RealityAgent will clean up the VC when the experience ends
    onClose();
  };

  // VC status label
  const vcStatusLabel = vcStatus?.phase === 'launching' ? 'Launching experiences...'
    : vcStatus?.phase === 'moving_to_vc' ? 'Moving to voice channel...'
    : vcStatus?.phase === 'done' ? 'All launched'
    : vcStatus?.phase === 'failed' ? 'Failed'
    : '';

  return (
    <div className="mps-overlay">
      <div className="mps-modal">
        {/* Header */}
        <div className="mps-header">
          <h3>{isVcMode ? experience.experiences?.name || 'Experience' : `Multiplayer: ${experience.experiences?.name || 'Experience'}`}</h3>
          <button className="mps-close-btn" onClick={handleClose} disabled={!!isActive}>
            &times;
          </button>
        </div>

        {/* Server Status (multiplayer only) */}
        {isMultiplayer && (
          <div className="mps-server-section">
            <div className="mps-section-label">Dedicated Server</div>
            {serverPc ? (
              <div className={`mps-server-info ${serverPc.status === 'online' ? 'online' : 'offline'}`}>
                <span className="mps-pc-name">{serverPc.pc_name || serverPc.id}</span>
                <span className={`mps-status-badge ${serverPc.status}`}>{serverPc.status}</span>
                {serverPc.ip_address && (
                  <span className="mps-ip">{serverPc.ip_address}</span>
                )}
              </div>
            ) : (
              <div className="mps-no-server">No dedicated server PC configured.</div>
            )}
          </div>
        )}

        {/* Language Selection (conditional) */}
        {needsLanguageSelect && (
          <div className="mps-language-section">
            <div className="mps-section-label">Language</div>
            <div className="mps-language-row">
              {supportedLanguages.map((langSupport: any) => {
                const shortName = langSupport.language?.short_name || '';
                const isSelected = selectedLanguage === shortName;
                return (
                  <button
                    key={langSupport.id}
                    className={`mps-language-btn ${isSelected ? 'selected' : ''}`}
                    onClick={() => setSelectedLanguage(shortName)}
                    disabled={!!isActive}
                  >
                    {shortName.toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* PC Selection */}
        <div className="mps-clients-section">
          <div className="mps-clients-header">
            <div className="mps-section-label">
              Select PCs ({selectedCount}/{selectablePcs.length})
            </div>
            <div className="mps-select-actions">
              <button onClick={selectAll} disabled={!!isActive}>All</button>
              <button onClick={deselectAll} disabled={!!isActive}>None</button>
            </div>
          </div>

          {!isVcMode && (
            <div className="mps-player-count">
              {selectedCount} selected (min: {minPlayers}, max: {maxPlayers})
              {selectedCount < minPlayers && (
                <span className="mps-warning"> — Need {minPlayers - selectedCount} more</span>
              )}
              {selectedCount > maxPlayers && (
                <span className="mps-warning"> — {selectedCount - maxPlayers} too many</span>
              )}
            </div>
          )}

          <div className="mps-pc-list">
            {selectablePcs.map((pc) => (
              <label
                key={pc.id}
                className={`mps-pc-item ${selectedPcIds.has(pc.id) ? 'selected' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selectedPcIds.has(pc.id)}
                  onChange={() => togglePc(pc.id)}
                  disabled={!!isActive}
                />
                <span className="mps-pc-name">
                  {pc.player_index != null ? `${pc.player_index}: ` : ''}{pc.pc_name || pc.id}
                </span>
                <span className={`mps-status-badge ${pc.status}`}>{pc.status}</span>
              </label>
            ))}
            {diagBlockedPcs.map((pc) => (
              <label key={pc.id} className="mps-pc-item diag-blocked">
                <input type="checkbox" checked={false} disabled readOnly />
                <span className="mps-pc-name">
                  {pc.player_index != null ? `${pc.player_index}: ` : ''}{pc.pc_name || pc.id}
                </span>
                <span className="mps-status-badge diag-blocked">checks failed</span>
              </label>
            ))}
            {selectablePcs.length === 0 && diagBlockedPcs.length === 0 && (
              <div className="mps-no-clients">No available PCs.</div>
            )}
          </div>
        </div>

        {/* Session Status — Multiplayer */}
        {!isVcMode && session && (
          <div className={`mps-session-status ${session.status}`}>
            <div className="mps-session-label">{STATUS_LABELS[session.status]}</div>
            {session.error && <div className="mps-session-error">{session.error}</div>}
            {session.status === 'waiting_for_server' && (
              <div className="mps-progress-bar">
                <div className="mps-progress-fill" />
              </div>
            )}
          </div>
        )}

        {/* Session Status — VC Mode */}
        {isVcMode && vcStatus && (
          <div className={`mps-session-status ${vcStatus.phase === 'failed' ? 'failed' : vcStatus.phase === 'done' ? 'active' : 'launching_clients'}`}>
            <div className="mps-session-label">{vcStatusLabel}</div>
            {vcStatus.error && <div className="mps-session-error">{vcStatus.error}</div>}
            {(vcStatus.phase === 'launching' || vcStatus.phase === 'moving_to_vc') && (
              <div className="mps-progress-bar">
                <div className="mps-progress-fill" />
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="mps-actions">
          {isVcMode ? (
            // VC mode actions
            !vcStatus || vcStatus.phase === 'failed' ? (
              <>
                <button className="mps-cancel-btn" onClick={handleClose}>Cancel</button>
                <button className="mps-go-btn" onClick={launchAndClose} disabled={!canLaunch}>
                  GO ({selectedCount} {selectedCount === 1 ? 'PC' : 'PCs'})
                </button>
              </>
            ) : vcStatus.phase === 'done' ? (
              <button className="mps-cancel-btn" onClick={handleClose}>Close</button>
            ) : (
              <button className="mps-cancel-btn" disabled>
                Launching...
              </button>
            )
          ) : (
            // Multiplayer mode actions
            !session || ['failed', 'completed'].includes(session.status) ? (
              <>
                <button className="mps-cancel-btn" onClick={handleClose}>Cancel</button>
                <button className="mps-go-btn" onClick={launchAndClose} disabled={!canLaunch}>
                  GO ({selectedCount} players)
                </button>
              </>
            ) : (
              <button className="mps-stop-btn" onClick={handleStop} disabled={session.status === 'stopping'}>
                Stop Session
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
